import { useState, useEffect } from 'react';
import { useProfile, useUpsertProfile, useUpdateMe } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

const EXPENSE_CATEGORIES = [
  { slug: 'housing',       label: '🏠 Housing / Rent',   isFixed: true  },
  { slug: 'food',          label: '🍽️ Food & Groceries',  isFixed: false },
  { slug: 'transport',     label: '🚗 Transport',          isFixed: false },
  { slug: 'utilities',     label: '💡 Utilities',          isFixed: true  },
  { slug: 'health',        label: '🏥 Health & Insurance', isFixed: false },
  { slug: 'entertainment', label: '🎬 Entertainment',      isFixed: false },
  { slug: 'subscriptions', label: '📱 Subscriptions',      isFixed: false },
  { slug: 'education',     label: '📚 Education',          isFixed: false },
  { slug: 'other',         label: '📦 Other',              isFixed: false },
];

const RISK_PROFILES = [
  { value: 'conservative', label: '🛡️ Conservative', description: 'Safety first — fixed income instruments' },
  { value: 'moderate',     label: '⚖️ Moderate',     description: 'Balanced portfolio — mix of fixed and variable income' },
  { value: 'aggressive',   label: '🚀 Aggressive',   description: 'Growth-focused — stocks, FIIs, higher volatility' },
];

const CURRENCIES = [
  { value: 'BRL', label: 'BRL — Brazilian Real (R$)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'GBP', label: 'GBP — British Pound (£)' },
  { value: 'MXN', label: 'MXN — Mexican Peso' },
  { value: 'ARS', label: 'ARS — Argentine Peso' },
  { value: 'CLP', label: 'CLP — Chilean Peso' },
  { value: 'COP', label: 'COP — Colombian Peso' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
];

const emptyExpenses = () =>
  EXPENSE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.slug]: '' }), {});

export default function ProfilePage() {
  const { user } = useAuthStore();
  const t = useT();
  const { data: profile, isLoading } = useProfile();
  const { mutateAsync: upsertProfile, isPending: isSavingProfile } = useUpsertProfile();
  const { mutateAsync: updateMe, isPending: isSavingUser } = useUpdateMe();
  const isSaving = isSavingProfile || isSavingUser;

  const [income, setIncome]           = useState('');
  const [savings, setSavings]         = useState('');
  const [riskProfile, setRiskProfile] = useState('moderate');
  const [currency, setCurrency]       = useState('BRL');
  const [expenses, setExpenses]       = useState(emptyExpenses());
  const [success, setSuccess]         = useState(false);
  const [error, setError]             = useState('');

  // Pre-populate form when profile loads
  useEffect(() => {
    if (!profile) return;
    setIncome(String(profile.monthly_income || ''));
    setSavings(String(profile.current_savings || ''));
    setRiskProfile(profile.risk_profile || 'moderate');
    setCurrency(profile.currency || user?.currency || 'BRL');

    const expMap = emptyExpenses();
    (profile.expenses || []).forEach((e) => {
      if (expMap.hasOwnProperty(e.category_slug)) {
        expMap[e.category_slug] = String(parseFloat(e.amount) || '');
      }
    });
    setExpenses(expMap);
  }, [profile]);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const parsedIncome = parseFloat(income);
    if (isNaN(parsedIncome) || parsedIncome <= 0) {
      setError(t('profile.error.income'));
      return;
    }

    const expenseList = Object.entries(expenses)
      .filter(([, v]) => v !== '' && parseFloat(v) > 0)
      .map(([slug, amount]) => {
        const cat = EXPENSE_CATEGORIES.find((c) => c.slug === slug);
        return {
          category_slug: slug,
          amount: parseFloat(amount),
          is_fixed: cat?.isFixed ?? false,
        };
      });

    try {
      await Promise.all([
        upsertProfile({
          monthly_income: parsedIncome,
          current_savings: parseFloat(savings) || 0,
          risk_profile: riskProfile,
          expenses: expenseList,
        }),
        updateMe({ currency }),
      ]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err.response?.data?.error?.message || t('profile.error.save'));
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-surface-card rounded-xl w-64" />
        {[1, 2, 3].map((i) => <div key={i} className="h-48 bg-surface-card rounded-2xl" />)}
      </div>
    );
  }

  const totalExpenses = Object.values(expenses)
    .reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const disposableIncome = Math.max(0, (parseFloat(income) || 0) - totalExpenses);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">{t('profile.title')}</h1>
        <p className="text-slate-400 mt-1">
          {t('profile.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Income & Currency */}
        <div className="card space-y-4">
          <h2 className="font-semibold text-slate-200 text-lg">{t('profile.income.title')}</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label={t('profile.income.monthly')}
              type="number"
              placeholder="e.g. 8000"
              min="0"
              step="0.01"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              required
            />
            <Input
              label={t('profile.income.savings')}
              type="number"
              placeholder="e.g. 15000"
              min="0"
              step="0.01"
              value={savings}
              onChange={(e) => setSavings(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">{t('profile.income.currency')}</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="input w-full md:w-72"
            >
              {CURRENCIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Summary */}
          {parseFloat(income) > 0 && (
            <div className="rounded-xl bg-surface p-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-500">{t('profile.summary.income')}</p>
                <p className="text-slate-200 font-semibold mt-0.5">
                  {parseFloat(income).toLocaleString('pt-BR', { style: 'currency', currency })}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('profile.summary.expenses')}</p>
                <p className="text-red-400 font-semibold mt-0.5">
                  −{totalExpenses.toLocaleString('pt-BR', { style: 'currency', currency })}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('profile.summary.disposable')}</p>
                <p className={`font-semibold mt-0.5 ${disposableIncome > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {disposableIncome.toLocaleString('pt-BR', { style: 'currency', currency })}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Monthly Expenses */}
        <div className="card space-y-4">
          <h2 className="font-semibold text-slate-200 text-lg">{t('profile.expenses.title')}</h2>
          <p className="text-sm text-slate-500">{t('profile.expenses.subtitle')}</p>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
            {EXPENSE_CATEGORIES.map((c) => (
              <div key={c.slug} className="flex items-center gap-3">
                <label className="text-sm text-slate-400 w-44 shrink-0">{t(`expense.cat.${c.slug}`)}</label>
                <input
                  type="number"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  value={expenses[c.slug]}
                  onChange={(ev) =>
                    setExpenses((prev) => ({ ...prev, [c.slug]: ev.target.value }))
                  }
                  className="input flex-1"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Risk Profile */}
        <div className="card space-y-4">
          <h2 className="font-semibold text-slate-200 text-lg">{t('profile.risk.title')}</h2>
          <p className="text-sm text-slate-500">{t('profile.risk.subtitle')}</p>
          <div className="grid md:grid-cols-3 gap-3">
            {RISK_PROFILES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setRiskProfile(r.value)}
                className={`p-4 rounded-xl text-left border transition-colors ${
                  riskProfile === r.value
                    ? 'bg-brand-600/10 border-brand-600 text-brand-300'
                    : 'bg-surface border-surface-border text-slate-400 hover:border-slate-500'
                }`}
              >
                <p className="font-medium">{t(`risk.${r.value}.label`)}</p>
                <p className="text-sm opacity-70 mt-1">{t(`risk.${r.value}.desc`)}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Feedback */}
        {error && (
          <div className="p-4 rounded-xl border border-red-700/50 bg-red-950/30 text-red-400 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 rounded-xl border border-green-700/50 bg-green-950/30 text-green-400 text-sm">
            ✓ {t('profile.success').replace('✓ ', '')}
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-4">
          <Button type="submit" loading={isSaving}>
            {t('profile.save')}
          </Button>
          {!isLoading && !profile && (
            <p className="text-sm text-amber-400">
              {t('profile.no_profile')}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
